import {
  colors,
  FileDisplaySection,
  FileDropZone,
  FileMimeType,
  FileWithUploadProgress,
  Icon,
  Loading,
  ModalHeader,
  Stack,
  TitleWithHelp,
} from '@ui-components/neo.tax';
import { ReactElement, useEffect, useMemo, useState } from 'react';

import { TabularDataParseError } from '@neo.tax/shared/graphql';
import { useAnalyticId } from '@neo.tax/shared/state';
import { interpretTabularDataParseError, validateFile } from '@neo.tax/shared/utils';

import { ScrollableUnorderedList } from '../../../../scrollable-unordered-list';
import { getProviderInfo } from '../../utils';
import { ConnectProviderStatus } from '../enums';
import { ConnectProviderSlideProps, ProviderAnalyticLiteralIds } from '../types';
import { StyledAlert, StyledCsvConnectProvider } from './connect-csv-provider.styled';
import { ConnectCsvProviderActions } from './ConnectCsvProviderActions';
import { useSyncCsvProvider } from './hooks';

const getAlertTitle = (connectStatus: ConnectProviderStatus): string => {
  switch (connectStatus) {
    case ConnectProviderStatus.SUCCESS:
      return 'Upload Success';
    case ConnectProviderStatus.ERROR:
      return 'There were errors with your upload';
    default:
      throw new Error('CSV alert component called with incorrect provider status.');
  }
};

const makeErrorMessages = (errors: TabularDataParseError[] | null | undefined): JSX.Element => {
  if (!errors) {
    return (
      <p>Unfortunately, we could not determine the error(s). Please review your file for erroneous or missing data.</p>
    );
  }

  if (errors.length === 1) {
    return <p>{interpretTabularDataParseError(errors[0])}</p>;
  }

  return (
    <ScrollableUnorderedList maxHeight={310}>
      {errors?.map((error, index) => (
        <li key={index}>{interpretTabularDataParseError(error)}</li>
      ))}
    </ScrollableUnorderedList>
  );
};

export const ConnectCsvProvider = ({
  close,
  fiscalYearId,
  restart,
  ...otherProps
}: ConnectProviderSlideProps['provider'] & {
  close: () => void;
  restart: () => void;
  fiscalYearId: string;
}): ReactElement => {
  const { DownloadCsvInstructionsComponent, analyticIds, fullName, helpIconHref, logo, name } =
    getProviderInfo(otherProps);
  const { connect, connectStatus, errors, file, retry, setFile } = useSyncCsvProvider({ ...otherProps, fiscalYearId });
  const [fileUploads, setFileUploads] = useState<FileWithUploadProgress[]>([]);
  const preCsvUpload =
    connectStatus === ConnectProviderStatus.FILE_ADDED ||
    connectStatus === ConnectProviderStatus.NOT_STARTED ||
    connectStatus === ConnectProviderStatus.WAITING_TOKEN;
  const showAlert = connectStatus === ConnectProviderStatus.SUCCESS || connectStatus === ConnectProviderStatus.ERROR;
  const showLoading =
    connectStatus === ConnectProviderStatus.WAITING_TOKEN || connectStatus === ConnectProviderStatus.SYNCING;

  if (!analyticIds.previousStepId) {
    throw new Error(`Please provide a 'previous step' id for ${name}`);
  }
  if (!analyticIds.uploadInputId) {
    throw new Error(`Please provide an 'upload input' id for ${name}`);
  }
  if (!analyticIds.closeAfterSuccessButtonId) {
    throw new Error(`Please provide an 'close after success button' id for ${name}`);
  }

  const literalAnalyticIds: ProviderAnalyticLiteralIds = {
    connectId: useAnalyticId(analyticIds.connectId),
    cancelConnectionId: useAnalyticId(analyticIds.cancelConnectionId),
    previousStepId: useAnalyticId(analyticIds.previousStepId),
    uploadInputId: useAnalyticId(analyticIds.uploadInputId),
    closeAfterSuccessButtonId: useAnalyticId(analyticIds.closeAfterSuccessButtonId),
  };

  useEffect(() => {
    const validateAndSetFileUploads = async (): Promise<void> => {
      if (!file) {
        setFileUploads([]);
        return;
      }

      const { errorMessage, status } = await validateFile(file);
      setFileUploads([
        {
          fileName: file.name,
          uploadPercent: showLoading ? 0 : null,
          status,
          errorMessage,
        },
      ]);
    };

    validateAndSetFileUploads();
  }, [file, showLoading]);
  const hasFileErrors = useMemo(() => fileUploads.some((file) => file.status === 'error'), [fileUploads]);

  return (
    <StyledCsvConnectProvider>
      <ModalHeader
        icon={<Icon node={logo} size={12} variant="none" />}
        iconBgColor={colors.white}
        title={
          helpIconHref ? (
            <TitleWithHelp title={`${fullName} Instructions`} href={helpIconHref} />
          ) : (
            `${fullName} Instructions`
          )
        }
      />
      {preCsvUpload && (
        <Stack direction="column" paddingY={4}>
          {DownloadCsvInstructionsComponent && <DownloadCsvInstructionsComponent />}
          <FileDropZone
            allowedMimeTypes={[FileMimeType.CSV]}
            addFiles={(files): void => setFile(files[0])}
            text="Your Report"
            allowMultipleSelect={false}
            inputId={literalAnalyticIds.uploadInputId}
          />
          <FileDisplaySection files={fileUploads} removeFile={(): void => setFile()} />
        </Stack>
      )}
      {showLoading && <Loading height={100} />}
      {showAlert && (
        <StyledAlert
          title={getAlertTitle(connectStatus)}
          subtitle={connectStatus === ConnectProviderStatus.ERROR ? makeErrorMessages(errors) : undefined}
          severity={connectStatus === ConnectProviderStatus.SUCCESS ? 'success' : 'error'}
        />
      )}
      <ConnectCsvProviderActions
        connectProvider={connect}
        close={close}
        restart={restart}
        retry={retry}
        providerName={name}
        status={connectStatus}
        analyticIds={literalAnalyticIds}
        isSubmitButtonDisabled={hasFileErrors}
      />
    </StyledCsvConnectProvider>
  );
};
